import { useEffect, useMemo, useState } from "react";
import { onValue, ref } from "firebase/database";
import { rtdb } from "../firebase";
import {
  WIKI_SCHEDULE_PATH,
  WIKI_SCHEDULE_STALE_MS,
  type WikiScheduleDay,
  type WikiSchedulePark,
} from "../config/themeparksWiki";
import { dayKeyInZone } from "../utils/calendarDay";

function localDateKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export interface UseParkScheduleResult {
  today: WikiScheduleDay | null;
  upcoming: WikiScheduleDay[];
  fetchedAt: number | null;
  stale: boolean;
  loading: boolean;
  /** The park's IANA zone, so its hours read on the park's clock. */
  timezone?: string;
}

/** Subscribe to a park's cached operating schedule (today + upcoming days). */
export function useParkSchedule(
  parkExternalId: string | null | undefined,
): UseParkScheduleResult {
  const [park, setPark] = useState<WikiSchedulePark | null>(null);
  const [loading, setLoading] = useState<boolean>(!!parkExternalId);

  useEffect(() => {
    if (!parkExternalId) {
      setPark(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const node = ref(rtdb, `${WIKI_SCHEDULE_PATH}/${parkExternalId}`);
    const unsubscribe = onValue(
      node,
      (snap) => {
        setPark((snap.val() as WikiSchedulePark | null) ?? null);
        setLoading(false);
      },
      () => {
        setPark(null);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [parkExternalId]);

  const { today, upcoming } = useMemo(() => {
    const days = park?.days ?? [];
    // "Today" at the park, not wherever the reader is: planning from Boston at
    // 10pm is still today in Anaheim.
    const todayKey = park?.timezone ? dayKeyInZone(new Date(), park.timezone) : localDateKey(new Date());
    const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date));
    const todays = sorted.filter((d) => d.date === todayKey);
    const todayDay = todays.find((d) => d.type === "OPERATING") ?? todays[0] ?? null;
    // Upcoming: dedupe by date (prefer the OPERATING entry), strictly after today, cap 7.
    const byDate = new Map<string, WikiScheduleDay>();
    for (const d of sorted) {
      if (d.date <= todayKey) continue;
      const existing = byDate.get(d.date);
      if (!existing || (d.type === "OPERATING" && existing.type !== "OPERATING")) {
        byDate.set(d.date, d);
      }
    }
    return { today: todayDay, upcoming: [...byDate.values()].slice(0, 7) };
  }, [park]);

  const fetchedAt = typeof park?.fetchedAt === "number" ? park.fetchedAt : null;
  return {
    today,
    upcoming,
    timezone: park?.timezone,
    fetchedAt,
    stale: fetchedAt != null && Date.now() - fetchedAt > WIKI_SCHEDULE_STALE_MS,
    loading,
  };
}
